import type { IMatchSummary } from "./types/IMatches";

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Match kickoff from API (ISO string). */
export function getMatchStartDate(m: IMatchSummary): Date | null {
  const raw = m.startDate ?? m.dateTime ?? null;
  if (!raw || typeof raw !== "string") return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function filterMatchesByDateRange(
  list: IMatchSummary[],
  range: { start: Date; end: Date } | null,
): IMatchSummary[] {
  if (!range) return list;
  const lo = startOfDay(range.start).getTime();
  const hi = startOfDay(range.end).getTime();
  return list.filter((m) => {
    const md = getMatchStartDate(m);
    if (!md) return true;
    const t = startOfDay(md).getTime();
    return t >= lo && t <= hi;
  });
}

function formatEndpoint(d: Date, other: Date): string {
  const sameYear = d.getFullYear() === other.getFullYear();
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/** Label for the date-range control: applied filter, or min–max of loaded matches. */
export function formatMatchesDateRangeLabel(
  appliedRange: { start: Date; end: Date } | null,
  matches: IMatchSummary[],
): string {
  if (appliedRange) {
    const s = startOfDay(appliedRange.start);
    const e = startOfDay(appliedRange.end);
    if (s.getTime() === e.getTime()) {
      return s.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
    return `${formatEndpoint(s, e)} - ${formatEndpoint(e, s)}`;
  }

  const dates = matches.map(getMatchStartDate).filter((x): x is Date => x != null);
  if (dates.length === 0) return "All dates";

  const dayTimes = dates.map((d) => startOfDay(d).getTime());
  const min = new Date(Math.min(...dayTimes));
  const max = new Date(Math.max(...dayTimes));
  if (min.getTime() === max.getTime()) {
    return min.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  return `${formatEndpoint(min, max)} - ${formatEndpoint(max, min)}`;
}

/** Case-insensitive match against team names, venue, tournament, type, result, umpire, etc. */
export function filterMatchesBySearchQuery(
  list: IMatchSummary[],
  rawQuery: string,
): IMatchSummary[] {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return list;

  const haystackFor = (m: IMatchSummary): string => {
    const parts: string[] = [
      m.teamOne?.teamName,
      m.teamTwo?.teamName,
      m.city,
      m.leagueName,
      m.matchType,
      m.tournament?.tournamentName,
      m.tournament?.matchType,
      m.matchResult,
      m.equation,
      m.tossResult,
      m.umpire,
      m.umpireName,
      m.groundId?.groundName,
      m.groundId?.groundAddress ?? undefined,
    ];
    for (const inn of m.inningScorecardList ?? []) {
      if (inn.battingTeamName) parts.push(inn.battingTeamName);
      if (inn.bowlingTeamName) parts.push(inn.bowlingTeamName);
    }
    return parts
      .filter((s): s is string => typeof s === "string" && s.length > 0)
      .join(" ")
      .toLowerCase();
  };

  return list.filter((m) => haystackFor(m).includes(q));
}

export function calendarYearsFromMatches(matches: IMatchSummary[]): number[] {
  const set = new Set<number>();
  const cy = new Date().getFullYear();
  for (let y = cy + 2; y >= cy - 8; y--) set.add(y);
  for (const m of matches) {
    const d = getMatchStartDate(m);
    if (d) set.add(d.getFullYear());
  }
  return Array.from(set).sort((a, b) => b - a);
}
